/* eslint-env es6 */
import { Subject } from 'rxjs';

import {
    followProfile,
    getBriefSocialProfileDto,
    getCurrentUserProfile,
    getFullUserProfile,
    getProfileAvatarImageId,
    getProfileFollowers,
    getProfileFollows,
    getProfilePhotoUrl,
    getStaticMapUrl,
    getUserProfileName,
    unfollowProfile,
    uploadProfilePhoto,
} from '@pressreader/social-api';
import { globalSettings } from '@pressreader/usersettings';
import { createSimpleCache } from '@pressreader/utils';

import ndUser from 'nd.user';
import { UserProfile } from 'nd.data.userprofile';
import { UserProfileFull } from 'nd.data.userprofile.full';
import FollowersInfo from 'nd.data.userprofile.followersinfo';
import { logError } from 'shared/errors/error.handlers';

// Thin adapter over @pressreader/social-api: wraps DTOs into the app's UserProfile
// classes and keeps the short-lived cache, followers-updates stream and user-state wiring.

const followersUpdatesSubject = new Subject();
const followersUpdates = followersUpdatesSubject.asObservable();

const cacheExpire = { expire: 1000 };
const cache = createSimpleCache(cacheExpire);

const cacheKeys = Object.freeze({
    profile: 'profile',
    fullProfile: 'fullprofile',
    profileName: 'profilename',
    profileCurrent: 'current',
});

function getProfile(profileId) {
    const key = `${cacheKeys.profile}.${profileId}`;
    let promise = cache.get(key);
    if (!promise) {
        promise = getBriefSocialProfileDto(profileId).then(data => new UserProfile(data.SocialInfo, data.OwnedPublications));
        cache.set(key, promise);
    }
    return promise;
}

function getProfileFull(profileId) {
    const key = `${cacheKeys.fullProfile}.${profileId}`;
    let promise = cache.get(key);
    if (!promise) {
        promise = getFullUserProfile(profileId).then(data => new UserProfileFull(data));
        cache.set(key, promise);
    }
    return promise;
}

function resetProfilesCache(profileId) {
    if (profileId === undefined) {
        cache.clear();
    } else {
        cache.remove(`${cacheKeys.fullProfile}.${profileId}`);
        cache.remove(`${cacheKeys.profile}.${profileId}`);
    }
}

function getProfileName(encryptedAccountNumber) {
    const key = `${cacheKeys.profileName}.${encryptedAccountNumber}`;
    let promise = cache.get(key);
    if (!promise) {
        promise = getUserProfileName(encryptedAccountNumber);
        cache.set(key, promise);
    }
    return promise;
}

function getCurrentProfile() {
    if (!ndUser.isLogin()) {
        return Promise.reject('Cannot get profile for anonymous user.');
    }
    const key = `${cacheKeys.profileCurrent}`;
    let promise = cache.get(key);
    if (!promise) {
        promise = getCurrentUserProfile().then(data => new UserProfile(data));
        cache.set(key, promise);
    }
    return promise;
}

function processFollowUnfollow(profileId, result) {
    if (!result || !result.ok) {
        return Promise.reject();
    }

    return getCurrentProfile().then(current => {
        if (current) {
            const currentProfileId = current.profileId;
            resetProfilesCache(currentProfileId);
            getProfileFull(currentProfileId)
                .then(data => {
                    followersUpdatesSubject.next({
                        profileId: currentProfileId,
                        followers: data.followers,
                    });
                })
                .catch(logError);
        }
        resetProfilesCache(profileId);
        globalSettings.reset();

        const info = new FollowersInfo(result.info);
        followersUpdatesSubject.next({
            profileId: profileId,
            followers: info,
        });
        return info;
    });
}

function followUser(profileId) {
    return followProfile(profileId).then(result => processFollowUnfollow(profileId, result));
}

function unFollowUser(profileId) {
    return unfollowProfile(profileId).then(result => processFollowUnfollow(profileId, result));
}

function getUserFollowers(profileId, filter, skip, take, orderBy) {
    return getProfileFollowers(profileId, { filter, skip, take, orderBy }).then(data => UserProfileFull.toPageSource(data));
}

function getUsersFollowed(profileId, filter, skip, take, orderBy) {
    return getProfileFollows(profileId, { filter, skip, take, orderBy }).then(data => UserProfileFull.toPageSource(data));
}

ndUser.onUserStateChanged(() => {
    // have to reset cached data once current user was changed
    // because followers-related information should be re-queried
    cache.remove(`${cacheKeys.profileCurrent}`);
    resetProfilesCache();
});

export default {
    followersUpdates,
    uploadProfilePhoto,
    getProfileFull,
    getProfile,
    getProfileName,
    getProfilePhotoUrl,
    getAvatarImageId: getProfileAvatarImageId,
    getCurrentProfile,
    getUserFollowers,
    getUsersFollowed,
    followUser,
    unFollowUser,
    getStaticMapUrl,
};
